import { useMemo } from 'react';
import { Menu, type MenuProps } from 'antd';
import {
  DashboardOutlined,
  ShoppingCartOutlined,
  DatabaseOutlined,
  WalletOutlined,
  RiseOutlined,
  SwapOutlined,
  UserOutlined,
  TeamOutlined,
  BankOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import type { ReportConfig } from '../types/panelConfig';

// Map icon names from backend to Ant Design icons
const iconMap: Record<string, React.ReactNode> = {
  ShoppingCartOutlined: <ShoppingCartOutlined />,
  DatabaseOutlined: <DatabaseOutlined />,
  WalletOutlined: <WalletOutlined />,
  RiseOutlined: <RiseOutlined />,
  SwapOutlined: <SwapOutlined />,
  UserOutlined: <UserOutlined />,
  TeamOutlined: <TeamOutlined />,
  BankOutlined: <BankOutlined />,
  TableOutlined: <TableOutlined />,
  DashboardOutlined: <DashboardOutlined />,
};

interface DynamicSidebarProps {
  reports: ReportConfig[];
}

export function DynamicSidebar({ reports }: DynamicSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = useMemo(() => {
    // Dashboard always first
    type MenuItem = NonNullable<MenuProps['items']>[number];
    const items: MenuItem[] = [
      { key: '/', icon: <DashboardOutlined />, label: 'Dashboard' },
    ];

    // Group reports by category
    const categories = new Map<string, ReportConfig[]>();
    for (const report of reports) {
      const existing = categories.get(report.category) || [];
      existing.push(report);
      categories.set(report.category, existing);
    }

    // If only one category, show flat list
    if (categories.size <= 1) {
      for (const report of reports) {
        items.push({
          key: `/rapor/${report.code}`,
          icon: iconMap[report.icon || ''] || <TableOutlined />,
          label: report.name,
        });
      }
    } else {
      // Multiple categories: use submenu groups
      for (const [category, categoryReports] of categories) {
        items.push({
          key: `category-${category}`,
          label: category,
          type: 'group' as const,
          children: categoryReports.map((report) => ({
            key: `/rapor/${report.code}`,
            icon: iconMap[report.icon || ''] || <TableOutlined />,
            label: report.name,
          })),
        });
      }
    }

    return items;
  }, [reports]);

  return (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={[location.pathname]}
      items={menuItems}
      onClick={({ key }) => navigate(key)}
    />
  );
}
